import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Trash2, Plus, TrendingUp, TrendingDown, Wallet, BarChart2, RepeatIcon } from 'lucide-react';
import {
    ResponsiveContainer, ComposedChart, BarChart, Bar, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell
} from 'recharts';

const API = process.env.REACT_APP_API_URL;

function useWindowWidth() {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handler = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return width;
}
const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const KATEGORIEN = ['Lebensmittel', 'Transport', 'Freizeit', 'Kleidung', 'Technik', 'Essen gehen', 'Sonstiges'];
const EINNAHMEN_KAT = ['Gehalt', 'Freelance', 'Taschengeld', 'Geschenk', 'Sonstiges'];
const ACCENT = '#fb923c';
const RED = '#ef4444';
const GREEN = '#4ade80';
const MUTED = '#a0a0a0';
const BORDER = '#2a2a2a';
const CARD = '#141414';

const fmtDay = (dateStr) => { const p = dateStr.split('-'); return `${parseInt(p[2])}.${parseInt(p[1])}`; };

const inputStyle = {
    background: 'var(--bg-secondary)', border: '2px solid var(--border)',
    borderRadius: '10px', color: 'var(--text-primary)', fontFamily: 'inherit',
    fontSize: '15px', padding: '12px 16px', width: '100%',
};
const selectStyle = { ...inputStyle, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' };

// ─── CUSTOM TOOLTIP ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    const isBar = payload[0]?.name === 'barValue';
    const items = isBar
        ? [
            payload[0].payload.Einkommen > 0 && { name: 'Einkommen', value: payload[0].payload.Einkommen, color: '#4ade80' },
            payload[0].payload.Ausgaben > 0 && { name: 'Ausgaben', value: payload[0].payload.Ausgaben, color: '#ef4444' },
          ].filter(Boolean)
        : payload.map(p => ({ name: p.name, value: p.value, color: p.color }));
    return (
        <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '12px 16px', fontSize: '13px' }}>
            <div style={{ fontWeight: 700, marginBottom: '8px', color: '#f5f5f5' }}>{label}</div>
            {items.map(p => (
                <div key={p.name} style={{ color: p.color, marginBottom: '2px' }}>
                    {p.name}: <strong>{Number(p.value).toFixed(2)}€</strong>
                </div>
            ))}
        </div>
    );
}

// ─── AUSGABEN TAB ──────────────────────────────────────────────────────────────
function AusgabenTab({ token }) {
    const isMobile = useWindowWidth() < 640;
    const now = new Date();
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year] = useState(now.getFullYear());
    const [ausgaben, setAusgaben] = useState([]);
    const [form, setForm] = useState({ day: String(now.getDate()), betrag: '', kategorie: 'Sonstiges', notiz: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchAusgaben = useCallback(async () => {
        const res = await fetch(`${API}/api/ausgaben`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setAusgaben(await res.json());
    }, [token]);

    useEffect(() => { fetchAusgaben(); }, [fetchAusgaben]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const day = parseInt(form.day);
        if (!form.day || day < 1 || day > 31) { setError('Gültigen Tag eingeben'); return; }
        const betragNorm = form.betrag.replace(',', '.');
        if (!form.betrag || parseFloat(betragNorm) <= 0 || isNaN(parseFloat(betragNorm))) { setError('Gültigen Betrag eingeben'); return; }
        setError('');
        setSaving(true);
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        await fetch(`${API}/api/ausgaben`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ date: dateStr, betrag: parseFloat(betragNorm), kategorie: form.kategorie, notiz: form.notiz, person: '' })
        });
        setForm(f => ({ ...f, betrag: '', notiz: '' }));
        setSaving(false);
        fetchAusgaben();
    };

    const handleDelete = async (id) => {
        await fetch(`${API}/api/ausgaben/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        fetchAusgaben();
    };

    const monthStr = `${year}-${String(month).padStart(2, '0')}`;
    const monthAusgaben = ausgaben.filter(a => a.date.startsWith(monthStr));
    const totalMonth = monthAusgaben.reduce((s, a) => s + a.betrag, 0);
    const byKategorie = monthAusgaben.reduce((acc, a) => { acc[a.kategorie] = (acc[a.kategorie] || 0) + a.betrag; return acc; }, {});

    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: '24px', maxWidth: '900px', margin: '0 auto 48px', alignItems: 'start' }}>
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px' }}>
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ flex: '0 0 70px' }}>
                                <label className="form-label">Tag</label>
                                <input type="text" inputMode="numeric" maxLength="2" value={form.day}
                                    onChange={e => setForm(f => ({ ...f, day: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                                    style={{ ...inputStyle, textAlign: 'center' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label className="form-label">Monat</label>
                                <select value={month} onChange={e => setMonth(Number(e.target.value))} style={selectStyle}>
                                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                </select>
                            </div>
                            <div style={{ flex: '0 0 90px', display: 'flex', flexDirection: 'column' }}>
                                <label className="form-label">Jahr</label>
                                <div style={{ ...inputStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', background: 'var(--bg-secondary)' }}>{year}</div>
                            </div>
                        </div>
                        <div>
                            <label className="form-label">Betrag (€)</label>
                            <input type="text" inputMode="decimal" value={form.betrag}
                                onChange={e => setForm(f => ({ ...f, betrag: e.target.value }))}
                                placeholder="0.00" style={inputStyle} />
                        </div>
                        <div>
                            <label className="form-label">Kategorie</label>
                            <select value={form.kategorie} onChange={e => setForm(f => ({ ...f, kategorie: e.target.value }))} style={selectStyle}>
                                {KATEGORIEN.map(k => <option key={k} value={k}>{k}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="form-label">Notiz <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(Optional)</span></label>
                            <input type="text" value={form.notiz} onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))}
                                placeholder="z.B. Rewe Einkauf" style={inputStyle} />
                        </div>
                        {error && <div style={{ color: '#ff5050', fontSize: '14px', padding: '10px', background: 'rgba(255,80,80,0.1)', borderRadius: '8px' }}>{error}</div>}
                        <button type="submit" className="btn-primary" disabled={saving}>
                            <Plus size={16} />{saving ? 'Wird gespeichert...' : 'Eintragen'}
                        </button>
                    </form>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--accent)', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{MONTHS[month - 1]} {year}</div>
                        <div style={{ fontSize: '36px', fontWeight: 800, color: 'var(--accent)' }}>{totalMonth.toFixed(2)}€</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>Gesamt</div>
                    </div>
                    {Object.entries(byKategorie).sort((a, b) => b[1] - a[1]).map(([k, t]) => (
                        <div key={k} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{k}</span>
                            <span style={{ fontSize: '14px', fontWeight: 600 }}>{t.toFixed(2)}€</span>
                        </div>
                    ))}
                </div>
            </div>
            {monthAusgaben.length > 0 && (
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '16px' }}>{MONTHS[month - 1]} {year}</h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {monthAusgaben.map(a => (
                            <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px', minWidth: '90px' }}>{a.date}</span>
                                    <span style={{ fontWeight: 700, color: 'var(--accent)', minWidth: '70px' }}>{a.betrag.toFixed(2)}€</span>
                                    <span style={{ fontSize: '12px', background: 'var(--bg-secondary)', padding: '2px 8px', borderRadius: '10px', color: 'var(--text-secondary)' }}>{a.kategorie}</span>
                                    {a.notiz && <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{a.notiz}</span>}
                                </div>
                                <button onClick={() => handleDelete(a.id)} className="icon-button" style={{ color: '#ff5050', flexShrink: 0 }}><Trash2 size={15} /></button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {monthAusgaben.length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px 0', fontSize: '14px', maxWidth: '900px', margin: '0 auto' }}>
                    Keine Ausgaben für {MONTHS[month - 1]} {year}
                </div>
            )}
        </div>
    );
}


// ─── MINI KALENDER ─────────────────────────────────────────────────────────────
function MiniKalender({ selectedMonat, ausgaben, einnahmen, fixkosten, fixeinkommen }) {
    const [yr, mo] = selectedMonat.split('-').map(Number);
    const [expanded, setExpanded] = React.useState({});
    const toggleExp = key => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

    const byDay = {};
    const ensure = d => { if (!byDay[d]) byDay[d] = { e: [], a: [] }; };
    einnahmen.forEach(e => { const d = parseInt(e.datum.split('-')[2]); ensure(d); byDay[d].e.push({ label: e.notiz || e.kategorie || 'Einnahme', betrag: e.betrag, kat: e.kategorie || 'Sonstiges' }); });
    ausgaben.forEach(a => { const d = parseInt(a.date.split('-')[2]); ensure(d); byDay[d].a.push({ label: a.notiz || a.kategorie, betrag: a.betrag, kat: a.kategorie || 'Sonstiges' }); });
    fixeinkommen.forEach(f => { const d = f.tag || 1; ensure(d); byDay[d].e.push({ label: f.name, betrag: f.betrag, kat: 'Fix' }); });
    fixkosten.forEach(f => { const d = f.tag || 1; ensure(d); byDay[d].a.push({ label: f.name, betrag: f.betrag, kat: 'Fix' }); });

    const activeDays = Object.keys(byDay).map(Number).sort((a, b) => a - b);
    if (activeDays.length === 0) return null;

    const today = new Date();
    const isCurrentMonth = today.getFullYear() === yr && today.getMonth() + 1 === mo;
    const todayDay = today.getDate();

    const pillGroup = (color, prefix, day, items) => {
        const groups = items.reduce((acc, item) => { (acc[item.kat] = acc[item.kat] || []).push(item); return acc; }, {});
        return Object.entries(groups).map(([kat, entries]) => {
            const total = entries.reduce((s, e) => s + e.betrag, 0);
            const key = `${day}-${prefix}-${kat}`;
            const isExp = expanded[key];
            return (
                <div key={key} style={{ marginBottom: '3px' }}>
                    <div onClick={() => entries.length > 1 && toggleExp(key)} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px',
                        background: `${color}18`, border: `1px solid ${color}40`, borderRadius: '6px',
                        padding: '3px 7px', cursor: entries.length > 1 ? 'pointer' : 'default' }}>
                        <span style={{ fontSize: '10px', color: '#c0c0c0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80px' }}>
                            {kat}{entries.length > 1 ? ` (${entries.length})` : ''}
                        </span>
                        <span style={{ fontSize: '11px', fontWeight: 700, color, whiteSpace: 'nowrap' }}>{prefix}{total.toFixed(2)}€</span>
                    </div>
                    {isExp && entries.map((e, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 7px 2px 14px', background: `${color}0a`, borderRadius: '4px', marginTop: '2px' }}>
                            <span style={{ fontSize: '10px', color: '#888' }}>{e.label}</span>
                            <span style={{ fontSize: '10px', color, fontWeight: 600 }}>{prefix}{e.betrag.toFixed(2)}€</span>
                        </div>
                    ))}
                </div>
            );
        });
    };

    return (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', justifyContent: 'center' }}>
            {activeDays.map(day => {
                const { e: eList, a: aList } = byDay[day];
                const isToday = isCurrentMonth && day === todayDay;
                return (
                    <div key={day} style={{
                        background: '#141414', border: `1px solid ${isToday ? 'rgba(251,146,60,0.5)' : '#2a2a2a'}`,
                        borderRadius: '10px', padding: '10px 12px', minWidth: '140px', flex: '0 0 auto',
                    }}>
                        <div style={{ fontSize: '11px', color: isToday ? '#fb923c' : '#606060', fontWeight: 700, marginBottom: '8px', textAlign: 'center' }}>
                            {day}.{mo}
                        </div>
                        {pillGroup('#4ade80', '+', day, eList)}
                        {pillGroup('#ef4444', '-', day, aList)}
                    </div>
                );
            })}
        </div>
    );
}


// ─── GRADIENT BAR SHAPE ────────────────────────────────────────────────────────
function GradientBar(props) {
    const { x, y, width, height, index, payload } = props;
    if (!payload || height <= 0 || width <= 0) return null;
    const { Ausgaben, Einkommen } = payload;
    const hasA = Ausgaben > 0;
    const hasE = Einkommen > 0;
    if (!hasA && !hasE) return null;
    const total = Ausgaben + Einkommen;
    const eRatio = total > 0 ? (Einkommen / total) * 100 : 0;
    const gradId = `tg-${index}`;
    const rx = 3;
    let fill;
    if (hasA && !hasE) fill = '#ef4444';
    else if (!hasA && hasE) fill = '#4ade80';
    else fill = `url(#${gradId})`;
    return (
        <g>
            {hasA && hasE && (
                <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4ade80" />
                        <stop offset={`${eRatio}%`} stopColor="#4ade80" />
                        <stop offset={`${eRatio}%`} stopColor="#ef4444" />
                        <stop offset="100%" stopColor="#ef4444" />
                    </linearGradient>
                </defs>
            )}
            <rect x={x + 1} y={y} width={Math.max(0, width - 2)} height={height} fill={fill} rx={rx} ry={rx} />
        </g>
    );
}


// ─── AUSGABEN GRUPPE ───────────────────────────────────────────────────────────
function AusgabenGruppe({ ausgaben, onDelete, selectedLabel }) {
    const [openKats, setOpenKats] = React.useState({});
    const toggle = k => setOpenKats(p => ({ ...p, [k]: !p[k] }));

    const grouped = ausgaben.reduce((acc, a) => {
        const k = a.kategorie || 'Sonstiges';
        if (!acc[k]) acc[k] = [];
        acc[k].push(a);
        return acc;
    }, {});

    return (
        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ fontSize: '12px', color: '#a0a0a0', fontWeight: 600, marginBottom: '4px' }}>
                {ausgaben.length} Ausgaben · {selectedLabel}
            </div>
            {Object.entries(grouped).map(([kat, items]) => {
                const total = items.reduce((s, a) => s + a.betrag, 0);
                const isOpen = openKats[kat];
                return (
                    <div key={kat}>
                        <div onClick={() => toggle(kat)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px', background: '#1a1a1a', borderRadius: '7px', border: '1px solid #2a2a2a',
                            cursor: 'pointer', userSelect: 'none' }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#d0d0d0' }}>{kat}</span>
                                <span style={{ fontSize: '11px', color: '#606060' }}>{items.length}x</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontWeight: 700, color: '#ef4444', fontSize: '13px' }}>-{total.toFixed(2)}€</span>
                                <span style={{ fontSize: '10px', color: '#606060' }}>{isOpen ? '▲' : '▼'}</span>
                            </div>
                        </div>
                        {isOpen && items.map(a => (
                            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '6px 12px 6px 20px', background: '#111', borderRadius: '6px',
                                border: '1px solid #222', marginTop: '2px' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <span style={{ color: '#606060', fontSize: '11px' }}>{fmtDay(a.date)}</span>
                                    <span style={{ fontWeight: 700, color: '#ef4444', fontSize: '12px' }}>-{a.betrag.toFixed(2)}€</span>
                                    {a.notiz && <span style={{ fontSize: '11px', color: '#808080' }}>{a.notiz}</span>}
                                </div>
                                <button onClick={() => onDelete(a.id)} className="icon-button" style={{ color: '#ff5050' }}><Trash2 size={12} /></button>
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

// ─── FINANZPLANER TAB ──────────────────────────────────────────────────────────
function FinanzplanerTab({ token, selectedMonat, setSelectedMonat, currentMonat }) {
    const isMobile = useWindowWidth() < 640;
    const now = new Date();

    const [ausgaben, setAusgaben] = useState([]);
    const [allEinnahmen, setAllEinnahmen] = useState([]);
    const [fixkosten, setFixkosten] = useState([]);
    const [fixeinkommen, setFixeinkommen] = useState([]);

    const [chartMode, setChartMode] = useState('verlauf');
    const [archivOpen, setArchivOpen] = useState(false);
    const [verlaufExclude] = useState(new Set(['2026-03']));

    const [verlaufZoom, setVerlaufZoom] = useState({ start: 0, end: -1 });
    const [tagesZoom, setTagesZoom] = useState({ start: 0, end: 31 });
    const [verlaufHover, setVerlaufHover] = useState(null);
    const [tagesHover, setTagesHover] = useState(null);
    const verlaufRef = useRef(null);
    const tagesRef = useRef(null);
    const verlaufDrag = useRef({ active: false, startX: 0, startZoom: null });
    const tagesDrag = useRef({ active: false, startX: 0, startZoom: null });

    useEffect(() => {
        setTagesZoom({ start: 0, end: 31 });
        setChartMode('monat');
    }, [selectedMonat]);

    const [eForm, setEForm] = useState({ day: String(now.getDate()), betrag: '', notiz: '', kategorie: 'Sonstiges' });
    const [eSaving, setESaving] = useState(false);
    const [aForm, setAForm] = useState({ day: String(now.getDate()), betrag: '', kategorie: 'Sonstiges', notiz: '' });
    const [aSaving, setASaving] = useState(false);
    const [aError, setAError] = useState('');
    const [fkForm, setFkForm] = useState({ name: '', betrag: '', tag: '1' });
    const [feForm, setFeForm] = useState({ name: '', betrag: '', tag: '1' });

    // ── Blur ────────────────────────────────────────────────────────────────────
    const [amountsBlurred, setAmountsBlurred] = useState(true);
    const blurStyle = (extra = {}) => ({
        filter: amountsBlurred ? 'blur(7px)' : 'none',
        cursor: 'pointer',
        userSelect: amountsBlurred ? 'none' : 'auto',
        transition: 'filter 0.25s ease',
        display: 'inline-block',
        ...extra
    });

    // ── Kontostand ──────────────────────────────────────────────────────────────
    const [kontostand, setKontostand] = useState(null);
    const [showKontoModal, setShowKontoModal] = useState(false);
    const [kontoInput, setKontoInput] = useState('');

    const loadKontostand = useCallback(async () => {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await fetch(`${API}/api/finanzplan?monat=${currentMonat}`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        if (data.kontostand !== null && data.kontostand !== undefined) {
            setKontostand(data.kontostand);
            setShowKontoModal(false);
        } else {
            // Vorschlag: Übrig des Vormonats aus DB laden
            const [y, m] = currentMonat.split('-');
            const prevDate = new Date(parseInt(y), parseInt(m) - 2, 1);
            const prevMonat = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
            const prevRes = await fetch(`${API}/api/finanzplan?monat=${prevMonat}`, { headers });
            let suggestion = '';
            if (prevRes.ok) {
                const prevData = await prevRes.json();
                if (prevData.uebrig !== null && prevData.uebrig !== undefined) {
                    suggestion = parseFloat(prevData.uebrig).toFixed(2);
                }
            }
            setKontoInput(suggestion);
            setShowKontoModal(true);
        }
    }, [token, currentMonat]);

    useEffect(() => { loadKontostand(); }, [loadKontostand]);

    const saveKontostand = () => {
        const val = parseFloat(kontoInput.replace(',', '.'));
        if (isNaN(val) || val < 0) return;
        setKontostand(val);
        setShowKontoModal(false);
        fetch(`${API}/api/finanzplan/kontostand`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ monat: currentMonat, kontostand: val })
        });
    };

    const fetchAll = useCallback(async () => {
        const headers = { Authorization: `Bearer ${token}` };
        const [aRes, eRes, fkRes, feRes] = await Promise.all([
            fetch(`${API}/api/ausgaben`, { headers }),
            fetch(`${API}/api/einnahmen/all`, { headers }),
            fetch(`${API}/api/fixkosten`, { headers }),
            fetch(`${API}/api/fixeinkommen`, { headers }),
        ]);
        if (aRes.ok) setAusgaben(await aRes.json());
        if (eRes.ok) setAllEinnahmen(await eRes.json());
        if (fkRes.ok) setFixkosten(await fkRes.json());
        if (feRes.ok) setFixeinkommen(await feRes.json());
    }, [token]);

    useEffect(() => { fetchAll(); }, [fetchAll]);
    useAutoRefresh(fetchAll, 15000);

    // Non-passive wheel → zoom, no page scroll
    useEffect(() => {
        const el = verlaufRef.current;
        if (!el) return;
        const handler = (e) => {
            e.preventDefault();
            const step = e.deltaY > 0 ? 2 : -2;
            setVerlaufZoom(prev => {
                const len = verlaufData.length;
                const curEnd = prev.end < 0 ? len - 1 : prev.end;
                const visible = curEnd - prev.start + 1;
                const newVisible = Math.max(2, Math.min(len, visible + step));
                const center = Math.round((prev.start + curEnd) / 2);
                const newStart = Math.max(0, center - Math.floor(newVisible / 2));
                const newEnd = Math.min(len - 1, newStart + newVisible - 1);
                return { start: newStart, end: newEnd };
            });
        };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler);
    }); // eslint-disable-line

    useEffect(() => {
        const el = tagesRef.current;
        if (!el) return;
        const handler = (e) => {
            e.preventDefault();
            const step = e.deltaY > 0 ? 2 : -2;
            setTagesZoom(prev => {
                const len = tagesData.length;
                const visible = prev.end - prev.start + 1;
                const newVisible = Math.max(2, Math.min(len, visible + step));
                const center = Math.round((prev.start + prev.end) / 2);
                const newStart = Math.max(0, center - Math.floor(newVisible / 2));
                const newEnd = Math.min(len - 1, newStart + newVisible - 1);
                return { start: newStart, end: newEnd };
            });
        };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler);
    }); // eslint-disable-line

    // Drag-to-pan helpers
    const startDrag = (dragRef, zoomState) => (e) => {
        dragRef.current = { active: true, startX: e.clientX, startZoom: { ...zoomState } };
    };
    const onDragMove = (dragRef, dataLen, setZoom) => (e) => {
        if (!dragRef.current.active) return;
        const el = dragRef.current.el || (dragRef.current.el = e.currentTarget);
        const rect = el.getBoundingClientRect();
        const { startX, startZoom } = dragRef.current;
        const curEnd = startZoom.end < 0 ? dataLen - 1 : startZoom.end;
        const visible = curEnd - startZoom.start + 1;
        const barWidth = rect.width / visible;
        const shift = -Math.round((e.clientX - startX) / barWidth);
        const newStart = Math.max(0, Math.min(dataLen - visible, startZoom.start + shift));
        setZoom({ start: newStart, end: Math.min(dataLen - 1, newStart + visible - 1) });
    };
    const stopDrag = (dragRef) => () => { dragRef.current.active = false; dragRef.current.el = null; };

    // Derived data
    const allMonths = useMemo(() => {
        const set = new Set();
        ausgaben.forEach(a => set.add(a.date.slice(0, 7)));
        allEinnahmen.forEach(e => set.add(e.datum.slice(0, 7)));
        set.add(currentMonat);
        return Array.from(set).sort();
    }, [ausgaben, allEinnahmen, currentMonat]);

    const isCurrentMonth = selectedMonat === currentMonat;
    const todayDay = now.getDate();

    // Alle Fixposten gesamt (für Prognose und Archiv-Monate)
    const fixAusgabenSumAll = useMemo(() => fixkosten.reduce((s, f) => s + f.betrag, 0), [fixkosten]);
    const fixEinkommenSumAll = useMemo(() => fixeinkommen.reduce((s, f) => s + f.betrag, 0), [fixeinkommen]);

    // Aktueller Monat: nur bereits fällige Fixposten (tag <= heute)
    const fixAusgabenSum = useMemo(() =>
        isCurrentMonth
            ? fixkosten.filter(f => (f.tag || 1) <= todayDay).reduce((s, f) => s + f.betrag, 0)
            : fixAusgabenSumAll,
    [fixkosten, isCurrentMonth, todayDay, fixAusgabenSumAll]);
    const fixEinkommenSum = useMemo(() =>
        isCurrentMonth
            ? fixeinkommen.filter(f => (f.tag || 1) <= todayDay).reduce((s, f) => s + f.betrag, 0)
            : fixEinkommenSumAll,
    [fixeinkommen, isCurrentMonth, todayDay, fixEinkommenSumAll]);

    const verlaufData = useMemo(() => {
        return allMonths.filter(monat => !verlaufExclude.has(monat)).map(monat => {
            const [y, mo] = monat.split('-');
            const label = `${MONTHS[parseInt(mo) - 1].slice(0, 3)} ${y.slice(2)}`;
            const monthAusg = ausgaben.filter(a => a.date.startsWith(monat)).reduce((s, a) => s + a.betrag, 0);
            const monthEink = allEinnahmen.filter(e => e.datum.startsWith(monat)).reduce((s, e) => s + e.betrag, 0);
            // Im Verlauf: aktueller Monat = nur fällige Fixkosten, Archiv = alle
            const fxA = monat === currentMonat ? fixAusgabenSum : fixAusgabenSumAll;
            const fxE = monat === currentMonat ? fixEinkommenSum : fixEinkommenSumAll;
            return {
                monat, label,
                Ausgaben: parseFloat((monthAusg + fxA).toFixed(2)),
                Einkommen: parseFloat((monthEink + fxE).toFixed(2))
            };
        });
    }, [allMonths, ausgaben, allEinnahmen, fixAusgabenSum, fixEinkommenSum, fixAusgabenSumAll, fixEinkommenSumAll, currentMonat]);

    const selectedAusgaben = useMemo(() =>
        ausgaben.filter(a => a.date.startsWith(selectedMonat)), [ausgaben, selectedMonat]);
    const selectedEinnahmen = useMemo(() =>
        allEinnahmen.filter(e => e.datum.startsWith(selectedMonat)), [allEinnahmen, selectedMonat]);

    const totalAusgaben = useMemo(() =>
        selectedAusgaben.reduce((s, a) => s + a.betrag, 0) + fixAusgabenSum, [selectedAusgaben, fixAusgabenSum]);
    const totalEinkommen = useMemo(() =>
        selectedEinnahmen.reduce((s, e) => s + e.betrag, 0) + fixEinkommenSum, [selectedEinnahmen, fixEinkommenSum]);
    const ersparnis = totalEinkommen - totalAusgaben;
    const ersparnisRate = totalEinkommen > 0 ? (ersparnis / totalEinkommen) * 100 : 0;

    // Prognose Monatsende (alle Fixposten)
    const progAusgaben = useMemo(() =>
        selectedAusgaben.reduce((s, a) => s + a.betrag, 0) + fixAusgabenSumAll, [selectedAusgaben, fixAusgabenSumAll]);
    const progEinkommen = useMemo(() =>
        selectedEinnahmen.reduce((s, e) => s + e.betrag, 0) + fixEinkommenSumAll, [selectedEinnahmen, fixEinkommenSumAll]);
    const projectedBalance = kontostand !== null ? kontostand + progEinkommen - progAusgaben : null;
    const currentBalance = kontostand !== null ? kontostand + totalEinkommen - totalAusgaben : null;

    // Übrig des aktuellen Monats in DB speichern (für Vorschlag im Folgemonat)
    useEffect(() => {
        if (currentBalance === null || !isCurrentMonth) return;
        const timer = setTimeout(() => {
            fetch(`${API}/api/finanzplan/uebrig`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ monat: currentMonat, uebrig: currentBalance })
            });
        }, 1500);
        return () => clearTimeout(timer);
    }, [currentBalance, currentMonat, isCurrentMonth, token]);

    const tagesData = useMemo(() => {
        const [yr, mn] = selectedMonat.split('-');
        const daysInMonth = new Date(parseInt(yr), parseInt(mn), 0).getDate();
        const dayAusg = {};
        const dayEink = {};
        selectedAusgaben.forEach(a => { const d = parseInt(a.date.split('-')[2]); dayAusg[d] = (dayAusg[d] || 0) + a.betrag; });
        selectedEinnahmen.forEach(e => { const d = parseInt(e.datum.split('-')[2]); dayEink[d] = (dayEink[d] || 0) + e.betrag; });
        fixkosten.forEach(f => { const d = f.tag || 1; dayAusg[d] = (dayAusg[d] || 0) + f.betrag; });
        fixeinkommen.forEach(f => { const d = f.tag || 1; dayEink[d] = (dayEink[d] || 0) + f.betrag; });
        return Array.from({ length: daysInMonth }, (_, i) => {
            const a = parseFloat((dayAusg[i + 1] || 0).toFixed(2));
            const e = parseFloat((dayEink[i + 1] || 0).toFixed(2));
            return { tag: i + 1, label: `${i+1}.${parseInt(mn)}`, Ausgaben: a, Einkommen: e, barValue: parseFloat((a + e).toFixed(2)) };
        });
    }, [selectedAusgaben, selectedEinnahmen, fixkosten, fixeinkommen, selectedMonat]);

    const visibleVerlauf = useMemo(() => {
        const end = verlaufZoom.end < 0 ? verlaufData.length - 1 : verlaufZoom.end;
        return verlaufData.slice(verlaufZoom.start, end + 1);
    }, [verlaufData, verlaufZoom]);

    const visibleTages = useMemo(() => {
        return tagesData.slice(tagesZoom.start, Math.min(tagesZoom.end + 1, tagesData.length));
    }, [tagesData, tagesZoom]);

    const handleBarClick = (data) => {
        if (data?.activePayload?.[0]?.payload?.monat) {
            setSelectedMonat(data.activePayload[0].payload.monat);
            setChartMode('monat');
        }
    };

    const addEinnahme = async (e) => {
        e.preventDefault();
        const day = parseInt(eForm.day);
        const eBetragNorm = eForm.betrag.replace(',', '.');
        if (!eForm.betrag || parseFloat(eBetragNorm) <= 0 || isNaN(parseFloat(eBetragNorm)) || !eForm.day || day < 1 || day > 31) return;
        setESaving(true);
        const [y, m] = selectedMonat.split('-');
        await fetch(`${API}/api/einnahmen`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ datum: `${y}-${m}-${String(day).padStart(2, '0')}`, betrag: parseFloat(eBetragNorm), notiz: eForm.notiz, kategorie: eForm.kategorie })
        });
        setEForm(f => ({ ...f, betrag: '', notiz: '', kategorie: 'Sonstiges' }));
        setESaving(false);
        fetchAll();
    };
    const deleteEinnahme = async (id) => {
        await fetch(`${API}/api/einnahmen/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        fetchAll();
    };
    const addAusgabe = async (e) => {
        e.preventDefault();
        const day = parseInt(aForm.day);
        const aBetragNorm = aForm.betrag.replace(',', '.');
        if (!aForm.betrag || parseFloat(aBetragNorm) <= 0 || isNaN(parseFloat(aBetragNorm)) || !aForm.day || day < 1 || day > 31) { setAError('Gültige Werte eingeben'); return; }
        setAError('');
        setASaving(true);
        const [y, m] = selectedMonat.split('-');
        await fetch(`${API}/api/ausgaben`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ date: `${y}-${m}-${String(day).padStart(2, '0')}`, betrag: parseFloat(aBetragNorm), kategorie: aForm.kategorie, notiz: aForm.notiz, person: '' })
        });
        setAForm(f => ({ ...f, betrag: '', notiz: '' }));
        setASaving(false);
        fetchAll();
    };
    const deleteAusgabe = async (id) => {
        await fetch(`${API}/api/ausgaben/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        fetchAll();
    };
    const addFixkost = async (e) => {
        e.preventDefault();
        const fkBetrag = fkForm.betrag.replace(',', '.');
        if (!fkForm.name || !fkForm.betrag || parseFloat(fkBetrag) <= 0 || isNaN(parseFloat(fkBetrag))) return;
        await fetch(`${API}/api/fixkosten`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name: fkForm.name, betrag: parseFloat(fkBetrag), tag: parseInt(fkForm.tag) || 1 })
        });
        setFkForm({ name: '', betrag: '', tag: '1' });
        fetchAll();
    };
    const deleteFixkost = async (id) => {
        await fetch(`${API}/api/fixkosten/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        fetchAll();
    };
    const addFixeinkommen = async (e) => {
        e.preventDefault();
        const feBetrag = feForm.betrag.replace(',', '.');
        if (!feForm.name || !feForm.betrag || parseFloat(feBetrag) <= 0 || isNaN(parseFloat(feBetrag))) return;
        await fetch(`${API}/api/fixeinkommen`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name: feForm.name, betrag: parseFloat(feBetrag), tag: parseInt(feForm.tag) || 1 })
        });
        setFeForm({ name: '', betrag: '', tag: '1' });
        fetchAll();
    };
    const deleteFixeinkommen = async (id) => {
        await fetch(`${API}/api/fixeinkommen/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        fetchAll();
    };

    const [yr, mo] = selectedMonat.split('-');
    const selectedLabel = `${MONTHS[parseInt(mo) - 1]} ${yr}`;

    const fixPanel = (title, items, sum, color, form, setForm, onAdd, onDelete, placeholder) => (
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <h3 style={{ fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <RepeatIcon size={15} color={color} /> {title}
            </h3>
            <form onSubmit={onAdd} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder={placeholder} style={{ ...inputStyle }} />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: '0 0 70px' }}>
                        <span style={{ fontSize: '11px', color: MUTED }}>Tag</span>
                        <input type="text" inputMode="numeric" maxLength="2" value={form.tag}
                            onChange={e => setForm(f => ({ ...f, tag: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                            placeholder="1" style={{ ...inputStyle, textAlign: 'center', padding: '10px 6px' }} />
                    </div>
                    <input type="text" inputMode="decimal" value={form.betrag} onChange={e => setForm(f => ({ ...f, betrag: e.target.value }))}
                        placeholder="Betrag €" style={{ ...inputStyle, flex: 1, marginTop: '17px' }} />
                    <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '12px 18px', flexShrink: 0, marginTop: '17px' }}>
                        <Plus size={15} />
                    </button>
                </div>
            </form>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', overflowY: 'auto', maxHeight: '260px' }}>
                {items.length === 0
                    ? <div style={{ color: MUTED, fontSize: '13px', textAlign: 'center', padding: '12px 0' }}>Noch keine Einträge</div>
                    : items.map(f => (
                        <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#111', borderRadius: '8px', border: `1px solid ${BORDER}` }}>
                            <div>
                                <div style={{ fontSize: '13px', fontWeight: 600 }}>{f.name}</div>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                                    <div style={{ fontSize: '14px', color, fontWeight: 700 }}>{f.betrag.toFixed(2)}€</div>
                                    {f.tag && <div style={{ fontSize: '11px', color: MUTED }}>am {f.tag}.</div>}
                                </div>
                            </div>
                            <button onClick={() => onDelete(f.id)} className="icon-button" style={{ color: '#ff5050' }}><Trash2 size={14} /></button>
                        </div>
                    ))
                }
            </div>
            {items.length > 0 && (
                <div style={{ fontSize: '13px', color: MUTED, textAlign: 'right', borderTop: `1px solid ${BORDER}`, paddingTop: '10px' }}>
                    Gesamt: <strong style={{ color, fontSize: '15px' }}>{sum.toFixed(2)}€</strong> / Monat
                </div>
            )}
        </div>
    );

    return (
        <div style={{ maxWidth: '1380px', margin: '0 auto' }}>

            {/* ── Kontostand Modal ──────────────────────────────────────────── */}
            {showKontoModal && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 999,
                    background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{ background: '#141414', border: '1px solid #333', borderRadius: '16px', padding: '36px 40px', maxWidth: '420px', width: '90%' }}>
                        <div style={{ fontSize: '11px', color: MUTED, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '8px' }}>
                            {(() => { const [y, m] = currentMonat.split('-'); return `${MONTHS[parseInt(m) - 1]} ${y}`; })()}
                        </div>
                        <h2 style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px', color: '#f5f5f5' }}>Kontostand eingeben</h2>
                        <p style={{ fontSize: '14px', color: MUTED, marginBottom: kontoInput ? '16px' : '28px', lineHeight: 1.6 }}>
                            Wie hoch ist dein aktueller Kontostand? Damit kann der Finanzplaner deine Ausgaben korrekt verrechnen und eine Prognose fürs Monatsende berechnen.
                        </p>
                        {kontoInput !== '' && (
                            <div style={{ fontSize: '12px', color: ACCENT, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>💡</span>
                                <span>Vorschlag: Übrig vom Vormonat ({kontoInput}€) – einfach anpassen oder bestätigen.</span>
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input
                                type="text" inputMode="decimal"
                                value={kontoInput}
                                onChange={e => setKontoInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && saveKontostand()}
                                placeholder="z.B. 1240,50"
                                autoFocus
                                style={{ ...inputStyle, fontSize: '18px', fontWeight: 700, flex: 1 }}
                            />
                            <span style={{ fontSize: '18px', fontWeight: 700, color: MUTED }}>€</span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                            <button onClick={saveKontostand} className="btn-primary" style={{ flex: 1, fontSize: '15px', padding: '13px' }}>
                                Speichern
                            </button>
                            <button onClick={() => setShowKontoModal(false)} className="icon-button"
                                style={{ border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '12px 18px', color: MUTED, fontSize: '14px' }}>
                                Später
                            </button>
                        </div>
                        {kontostand !== null && (
                            <div style={{ marginTop: '16px', fontSize: '12px', color: MUTED, textAlign: 'center' }}>
                                Aktuell gespeichert: <strong style={{ color: '#f5f5f5' }}>{kontostand.toFixed(2)}€</strong>
                                <button onClick={() => setShowKontoModal(false)} style={{ background: 'none', border: 'none', color: ACCENT, cursor: 'pointer', marginLeft: '8px', fontSize: '12px' }}>beibehalten</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Monat Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '14px' }}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: '#f5f5f5' }}>
                    {(() => { const [y, m] = selectedMonat.split('-'); return `${MONTHS[parseInt(m) - 1]} ${y}`; })()}
                </div>
                {allMonths.filter(mo2 => mo2 !== currentMonat).length > 0 && (
                    <div style={{ position: 'relative' }}>
                        <button onClick={() => setArchivOpen(o => !o)} style={{
                            fontSize: '13px', fontWeight: 600, padding: '6px 14px', borderRadius: '8px',
                            cursor: 'pointer', fontFamily: 'inherit', background: archivOpen ? 'var(--bg-secondary)' : 'transparent',
                            color: archivOpen ? '#f5f5f5' : MUTED, border: `1px solid ${archivOpen ? '#555' : BORDER}`, transition: 'all 0.2s',
                        }}>
                            Archiv
                        </button>
                        {archivOpen && (
                            <div style={{
                                position: 'absolute', top: '110%', right: 0, zIndex: 100,
                                background: '#1a1a1a', border: `1px solid ${BORDER}`, borderRadius: '10px',
                                padding: '8px', minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '4px',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                            }}>
                                {allMonths.filter(mo2 => mo2 !== currentMonat).reverse().map(mo2 => {
                                    const [y2, m2] = mo2.split('-');
                                    const isSelected = mo2 === selectedMonat;
                                    return (
                                        <button key={mo2} onClick={() => { setSelectedMonat(mo2); setChartMode('monat'); setArchivOpen(false); }}
                                            style={{
                                                fontSize: '13px', fontWeight: isSelected ? 700 : 500, padding: '8px 12px',
                                                borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                                                background: isSelected ? 'rgba(251,146,60,0.1)' : 'transparent',
                                                color: isSelected ? ACCENT : '#d0d0d0',
                                                border: `1px solid ${isSelected ? ACCENT + '60' : 'transparent'}`,
                                            }}>
                                            {MONTHS[parseInt(m2) - 1]} {y2}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Kalender */}
            <MiniKalender
                selectedMonat={selectedMonat}
                ausgaben={selectedAusgaben}
                einnahmen={selectedEinnahmen}
                fixkosten={fixkosten}
                fixeinkommen={fixeinkommen}
            />

            {/* Summary */}
            {isCurrentMonth && kontostand !== null ? (
                /* ── Aktueller Monat mit Kontostand ── */
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1.4fr 1fr 1fr 1.4fr', gap: '14px', marginBottom: '20px' }}>
                    {/* Kontostand Start */}
                    <div style={{ background: CARD, border: `1px solid #3b82f6`, borderRadius: '12px', padding: '18px', position: 'relative' }}>
                        <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.06em' }}>Kontostand (Anfang)</div>
                        <div style={{ fontSize: '26px', fontWeight: 800, color: '#3b82f6' }}>
                            <span onClick={() => setAmountsBlurred(v => !v)} style={blurStyle()} title={amountsBlurred ? 'Klicken zum Anzeigen' : 'Klicken zum Verbergen'}>{kontostand.toFixed(2)}€</span>
                        </div>
                        <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>eingetragen zum Monatsstart</div>
                        <button onClick={() => setShowKontoModal(true)}
                            style={{ position: 'absolute', top: '10px', right: '12px', background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '15px' }} title="Ändern">✎</button>
                    </div>
                    {/* Einnahmen */}
                    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: GREEN, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.06em' }}>+ Einnahmen</div>
                        <div style={{ fontSize: '22px', fontWeight: 800, color: GREEN }}>{totalEinkommen.toFixed(2)}€</div>
                        {fixEinkommenSum > 0 && <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>inkl. {fixEinkommenSum.toFixed(2)}€ fix</div>}
                    </div>
                    {/* Ausgaben */}
                    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: RED, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.06em' }}>− Ausgaben</div>
                        <div style={{ fontSize: '22px', fontWeight: 800, color: RED }}>{totalAusgaben.toFixed(2)}€</div>
                        {fixAusgabenSum > 0 && <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>inkl. {fixAusgabenSum.toFixed(2)}€ fix (fällig)</div>}
                    </div>
                    {/* Übrig + Prognose */}
                    <div style={{ background: CARD, border: `2px solid ${currentBalance !== null && currentBalance >= 0 ? ACCENT : RED}`, borderRadius: '12px', padding: '18px', position: 'relative' }}>
                        <div style={{ fontSize: '11px', color: ACCENT, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.06em' }}>Übrig jetzt</div>
                        <div style={{ fontSize: '28px', fontWeight: 900, color: currentBalance !== null && currentBalance >= 0 ? ACCENT : RED }}>
                            <span onClick={() => setAmountsBlurred(v => !v)} style={blurStyle()} title={amountsBlurred ? 'Klicken zum Anzeigen' : 'Klicken zum Verbergen'}>
                                {currentBalance !== null ? `${currentBalance.toFixed(2)}€` : '—'}
                            </span>
                        </div>
                        {projectedBalance !== null && (
                            <div style={{ fontSize: '12px', color: MUTED, marginTop: '6px', borderTop: `1px solid ${BORDER}`, paddingTop: '6px' }}>
                                Prognose Monatsende: <strong style={{ color: projectedBalance >= 0 ? '#a78bfa' : RED }}>
                                    <span onClick={() => setAmountsBlurred(v => !v)} style={blurStyle()} title={amountsBlurred ? 'Klicken zum Anzeigen' : 'Klicken zum Verbergen'}>{projectedBalance.toFixed(2)}€</span>
                                </strong>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                /* ── Archiv-Monat ohne Kontostand ── */
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                    <div style={{ background: CARD, border: `1px solid ${ACCENT}`, borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: MUTED, marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}><TrendingUp size={13} /> Einkommen</div>
                        <div style={{ fontSize: '24px', fontWeight: 800, color: ACCENT }}>{totalEinkommen.toFixed(2)}€</div>
                        {fixEinkommenSum > 0 && <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>inkl. {fixEinkommenSum.toFixed(2)}€ fix</div>}
                    </div>
                    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: MUTED, marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}><TrendingDown size={13} /> Ausgaben</div>
                        <div style={{ fontSize: '24px', fontWeight: 800, color: '#f5f5f5' }}>{totalAusgaben.toFixed(2)}€</div>
                        {fixAusgabenSum > 0 && <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>inkl. {fixAusgabenSum.toFixed(2)}€ fix</div>}
                    </div>
                    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: MUTED, marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}><Wallet size={13} /> Ersparnis</div>
                        <div style={{ fontSize: '24px', fontWeight: 800, color: ersparnis >= 0 ? GREEN : RED }}>
                            {ersparnis >= 0 ? '+' : ''}{ersparnis.toFixed(2)}€
                        </div>
                        {totalEinkommen > 0 && <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>{ersparnisRate.toFixed(1)}% gespart</div>}
                    </div>
                </div>
            )}

            {/* Fix panels LEFT + Chart + Fix panels RIGHT */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr 1fr', gap: '20px', marginBottom: '20px', alignItems: 'start' }}>
                {/* Fix Einkommen – links */}
                {fixPanel('Fixes Einkommen / Mo', fixeinkommen, fixEinkommenSumAll, GREEN, feForm, setFeForm, addFixeinkommen, deleteFixeinkommen, 'z.B. Gehalt')}

                {/* Chart – mitte */}
                <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                        <h3 style={{ fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                            <BarChart2 size={15} color={ACCENT} />
                            {chartMode === 'verlauf' ? 'Verlauf aller Monate' : `Tage – ${selectedLabel}`}
                        </h3>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            {['verlauf', 'monat'].map(mode => (
                                <button key={mode} onClick={() => setChartMode(mode)} style={{
                                    padding: '4px 12px', borderRadius: '7px', fontWeight: 600, fontSize: '12px',
                                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                                    background: chartMode === mode ? ACCENT : 'var(--bg-secondary)',
                                    color: chartMode === mode ? '#000' : MUTED,
                                    border: `1px solid ${chartMode === mode ? ACCENT : BORDER}`,
                                }}>
                                    {mode === 'verlauf' ? 'Gesamt' : selectedLabel}
                                </button>
                            ))}
                        </div>
                    </div>

                    {chartMode === 'verlauf' ? (
                        verlaufData.length === 0
                            ? <div style={{ textAlign: 'center', color: MUTED, padding: '60px 0', fontSize: '13px' }}>Noch keine Daten</div>
                            : <div ref={verlaufRef} style={{ userSelect: 'none', cursor: verlaufDrag.current?.active ? 'grabbing' : 'grab' }}
                                onMouseDown={startDrag(verlaufDrag, verlaufZoom)}
                                onMouseMove={onDragMove(verlaufDrag, verlaufData.length, setVerlaufZoom)}
                                onMouseUp={stopDrag(verlaufDrag)}
                                onMouseLeave={stopDrag(verlaufDrag)}>
                                <ResponsiveContainer width="100%" height={280}>
                                    <ComposedChart data={visibleVerlauf} onClick={handleBarClick} style={{ cursor: 'inherit' }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                                        <XAxis dataKey="label" tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}€`} width={65} />
                                        <Tooltip content={<CustomTooltip />} cursor={false} />
                                        <Legend wrapperStyle={{ fontSize: '12px', color: MUTED, paddingTop: '6px' }} />
                                        <Bar dataKey="Ausgaben" radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}
                                            onMouseOver={(_, index) => setVerlaufHover(index)}
                                            onMouseLeave={() => setVerlaufHover(null)}>
                                            {visibleVerlauf.map((_, i) => (
                                                <Cell key={i} fill={RED} fillOpacity={verlaufHover === null || verlaufHover === i ? 1 : 0.3} />
                                            ))}
                                        </Bar>
                                        <Line dataKey="Einkommen" stroke={GREEN} strokeWidth={2} dot={{ fill: GREEN, r: 3 }} activeDot={{ r: 5 }} type="monotone" />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                    ) : (
                        tagesData.every(d => d.Ausgaben === 0 && d.Einkommen === 0)
                            ? <div style={{ textAlign: 'center', color: MUTED, padding: '60px 0', fontSize: '13px' }}>Keine Einträge für {selectedLabel}</div>
                            : <div ref={tagesRef} style={{ userSelect: 'none', cursor: tagesDrag.current?.active ? 'grabbing' : 'grab' }}
                                onMouseDown={startDrag(tagesDrag, tagesZoom)}
                                onMouseMove={onDragMove(tagesDrag, tagesData.length, setTagesZoom)}
                                onMouseUp={stopDrag(tagesDrag)}
                                onMouseLeave={stopDrag(tagesDrag)}>
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={visibleTages} style={{ cursor: 'inherit' }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                                        <XAxis dataKey="label" tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                        <YAxis tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => v > 0 ? `${v}€` : ''} width={60} />
                                        <Tooltip content={<CustomTooltip />} cursor={false} />
                                        <Bar dataKey="barValue" maxBarSize={22} isAnimationActive={false}
                                            shape={<GradientBar />}
                                            onMouseOver={(_, index) => setTagesHover(index)}
                                            onMouseLeave={() => setTagesHover(null)}
                                            fillOpacity={1}>
                                            {visibleTages.map((d, i) => (
                                                <Cell key={i} fillOpacity={d.barValue === 0 ? 0 : tagesHover === null || tagesHover === i ? 1 : 0.35} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                    )}
                </div>

                {/* Fix Ausgaben – rechts */}
                {fixPanel('Fixe Ausgaben / Mo', fixkosten, fixAusgabenSumAll, RED, fkForm, setFkForm, addFixkost, deleteFixkost, 'z.B. Miete')}
            </div>

            {/* Einnahmen + Ausgaben eintragen */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                {/* Einnahmen */}
                <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '20px' }}>
                    <h3 style={{ fontWeight: 700, fontSize: '14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <TrendingUp size={15} color={GREEN} /> Einnahme eintragen
                    </h3>
                    <form onSubmit={addEinnahme} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ flex: '0 0 65px' }}>
                                <label className="form-label">Tag</label>
                                <input type="text" inputMode="numeric" maxLength="2" value={eForm.day}
                                    onChange={e => setEForm(f => ({ ...f, day: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                                    style={{ ...inputStyle, textAlign: 'center', padding: '10px 8px' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label className="form-label">Betrag (€)</label>
                                <input type="text" inputMode="decimal" value={eForm.betrag}
                                    onChange={e => setEForm(f => ({ ...f, betrag: e.target.value }))}
                                    placeholder="0.00" style={{ ...inputStyle, padding: '10px 14px' }} />
                            </div>
                        </div>
                        <div>
                            <label className="form-label">Kategorie</label>
                            <select value={eForm.kategorie} onChange={e => setEForm(f => ({ ...f, kategorie: e.target.value }))}
                                style={{ ...selectStyle, padding: '10px 14px' }}>
                                {EINNAHMEN_KAT.map(k => <option key={k} value={k}>{k}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="form-label">Notiz <span style={{ color: MUTED, fontWeight: 400 }}>(Optional)</span></label>
                            <input type="text" value={eForm.notiz} onChange={e => setEForm(f => ({ ...f, notiz: e.target.value }))}
                                placeholder="z.B. April" style={{ ...inputStyle, padding: '10px 14px' }} />
                        </div>
                        <button type="submit" disabled={eSaving} className="btn-primary" style={{ padding: '10px' }}>
                            <Plus size={14} />{eSaving ? '...' : 'Hinzufügen'}
                        </button>
                    </form>
                    {selectedEinnahmen.length > 0 && (
                        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <div style={{ fontSize: '12px', color: MUTED, fontWeight: 600, marginBottom: '4px' }}>
                                {selectedEinnahmen.length} Einnahmen · {selectedLabel}
                            </div>
                            {selectedEinnahmen.map(e => (
                                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#111', borderRadius: '7px', border: `1px solid ${BORDER}` }}>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <span style={{ color: MUTED, fontSize: '12px' }}>{fmtDay(e.datum)}</span>
                                        <span style={{ fontSize: '11px', color: '#808080' }}>{e.kategorie || 'Sonstiges'}</span>
                                        <span style={{ fontWeight: 700, color: GREEN }}>{e.betrag.toFixed(2)}€</span>
                                        {e.notiz && <span style={{ fontSize: '11px', color: MUTED }}>{e.notiz}</span>}
                                    </div>
                                    <button onClick={() => deleteEinnahme(e.id)} className="icon-button" style={{ color: '#ff5050' }}><Trash2 size={13} /></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Ausgaben */}
                <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '20px' }}>
                    <h3 style={{ fontWeight: 700, fontSize: '14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <TrendingDown size={15} color={RED} /> Ausgabe eintragen
                    </h3>
                    <form onSubmit={addAusgabe} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ flex: '0 0 65px' }}>
                                <label className="form-label">Tag</label>
                                <input type="text" inputMode="numeric" maxLength="2" value={aForm.day}
                                    onChange={e => setAForm(f => ({ ...f, day: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                                    style={{ ...inputStyle, textAlign: 'center', padding: '10px 8px' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label className="form-label">Betrag (€)</label>
                                <input type="text" inputMode="decimal" value={aForm.betrag}
                                    onChange={e => setAForm(f => ({ ...f, betrag: e.target.value }))}
                                    placeholder="0.00" style={{ ...inputStyle, padding: '10px 14px' }} />
                            </div>
                        </div>
                        <div>
                            <label className="form-label">Kategorie</label>
                            <select value={aForm.kategorie} onChange={e => setAForm(f => ({ ...f, kategorie: e.target.value }))}
                                style={{ ...selectStyle, padding: '10px 14px' }}>
                                {KATEGORIEN.map(k => <option key={k} value={k}>{k}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="form-label">Notiz <span style={{ color: MUTED, fontWeight: 400 }}>(Optional)</span></label>
                            <input type="text" value={aForm.notiz} onChange={e => setAForm(f => ({ ...f, notiz: e.target.value }))}
                                placeholder="z.B. Rewe Einkauf" style={{ ...inputStyle, padding: '10px 14px' }} />
                        </div>
                        {aError && <div style={{ color: '#ff5050', fontSize: '13px', padding: '8px', background: 'rgba(255,80,80,0.1)', borderRadius: '8px' }}>{aError}</div>}
                        <button type="submit" disabled={aSaving} className="btn-primary" style={{ padding: '10px' }}>
                            <Plus size={14} />{aSaving ? '...' : 'Eintragen'}
                        </button>
                    </form>
                    {selectedAusgaben.length > 0 && (
                        <AusgabenGruppe ausgaben={selectedAusgaben} onDelete={deleteAusgabe} selectedLabel={selectedLabel} />
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function Ausgaben() {
    const { user } = useAuth();
    const token = user?.token;
    const [view, setViewState] = useState(() => localStorage.getItem('ausgaben_view') || null);
    const setView = (v) => { setViewState(v); if (v) localStorage.setItem('ausgaben_view', v); else localStorage.removeItem('ausgaben_view'); };
    const now = new Date();
    const currentMonat = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const [selectedMonat, setSelectedMonat] = useState(currentMonat);

    const BEREICHE = [
        { key: 'ausgaben', title: 'Ausgaben', icon: Wallet, desc: 'Ausgaben pro Monat nach Kategorie und Person erfassen und auswerten.' },
        { key: 'finanzplaner', title: 'Finanzplaner', icon: BarChart2, desc: 'Einkommen tracken, interaktive Charts und Sparquote im Blick behalten.' },
    ];

    if (!view) {
        return (
            <div className="section" style={{ minHeight: 'calc(100vh - 140px)' }}>
                <div className="container">
                    <Link to="/privat" className="icon-button" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', marginBottom: '32px' }}>
                        <ArrowLeft size={20} />
                        <span style={{ fontWeight: 500 }}>Zurück</span>
                    </Link>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                        <h1 className="section-title">Ausgaben</h1>
                        <p className="section-subtitle" style={{ marginBottom: '40px' }}>Ausgaben verwalten oder Finanzen planen</p>
                        <div className="projects-grid" style={{ maxWidth: '600px', margin: '0 auto' }}>
                            {BEREICHE.map((b, index) => {
                                const Icon = b.icon;
                                return (
                                    <motion.div key={b.key} className="project-card"
                                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        style={{ cursor: 'pointer' }} onClick={() => setView(b.key)}>
                                        <div className="project-image">
                                            <Icon size={56} strokeWidth={1.5} color="var(--text-secondary)" />
                                        </div>
                                        <div className="project-content">
                                            <h3 className="project-title">{b.title}</h3>
                                            <p className="project-description">{b.desc}</p>
                                            <div className="project-links">
                                                <span className="project-link" style={{ flex: 1, justifyContent: 'center' }}>Öffnen</span>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="section" style={{ minHeight: 'calc(100vh - 140px)' }}>
            <div className="container" style={view === 'finanzplaner' ? { maxWidth: '1400px' } : {}}>
                <button onClick={() => {
                    if (view === 'finanzplaner' && selectedMonat !== currentMonat) {
                        setSelectedMonat(currentMonat);
                    } else {
                        setView(null);
                    }
                }} className="icon-button"
                    style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', marginBottom: '32px' }}>
                    <ArrowLeft size={20} />
                    <span style={{ fontWeight: 500 }}>Zurück</span>
                </button>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <h1 className="section-title">{view === 'ausgaben' ? 'Ausgaben' : 'Finanzplaner'}</h1>
                    {view === 'ausgaben' && <AusgabenTab token={token} />}
                    {view === 'finanzplaner' && <FinanzplanerTab token={token} selectedMonat={selectedMonat} setSelectedMonat={setSelectedMonat} currentMonat={currentMonat} />}
                </motion.div>
            </div>
        </div>
    );
}
